import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';

import { CKEditorUtils } from '../../util/CKEditorUtils';
import {
  configureBalloonPanels,
  configureQualioPluginsForTrackChanges,
  EditorMode,
  enableUploadAdapter,
  isCommentsOnlyMode,
  setCommentsOnlyModeTo,
  toggleCheckboxMode,
  toggleTrackChanges,
} from '../../util/EditModeUtils';
import {
  extractAttachmentIdFromHref,
  getBookmarkTarget,
  getHTMLElement,
  getHTMLElements,
  scrollToBookmarkTarget,
  setElementDisabledState,
} from '../../util/HTMLElementUtils';
import { refreshDisplayMode } from '../../util/SidebarUtils';
import { ToolbarManager } from '../../util/ToolbarManager';

import {
  Context,
  ContextPlugin,
  Editor,
  EventInfo,
  MultiRootEditor,
  Rect,
  ViewElement,
} from 'ckeditor5';
import { Annotation } from 'ckeditor5-premium-features';
import { SetStateAction } from 'react';
import { PageOrientation } from '../../api/model/document';
import {
  EDITOR_CONTEXT_READY_ERROR,
  EDITOR_READY_ERROR,
  logCustomError,
} from '../../messages/LogErrorMessages';
import { EditorStatus } from '../../views/components/DocumentOverview/Context';
import { configureChangedRootsTracker } from '../../views/reducers/DocumentReducer';
import {
  handleComment,
  setupDeletedByUserHandlers,
} from './CommentEventHandler';
import { setupSuggestionUpdateHandler } from './SuggestionEventHandler';

export type EditorName =
  | 'documentEditorContent'
  | 'changeControlEditorContent'
  | 'documentHTMLViewerContent';

export const createContextReadyHandler = (
  currentUser: CurrentUser,
  readyHandler: (editors: Editor[]) => void,
  commentsPermissions: number,
  toolbarManager: ToolbarManager,
  createNewCommentButton: string,
  setAttachmentForPreview: (id: string) => void,
  editorName: EditorName,
  setEditorStatus: React.Dispatch<SetStateAction<EditorStatus>>,
  handleHighlighting: (
    annotation: any,
    identifierPredicate: (annotation: Annotation) => boolean,
    htmlSelector: string,
  ) => NodeJS.Timeout,
  setDidReceiveDisconnectionEvent?: (didReceive: boolean) => void,
  targetId?: string,
) => {
  return (context: Context) => {
    try {
      const { editors } = context;
      const editorsArr: Editor[] = Array.from(editors);
      readyHandler(editorsArr);
      setupCommentButton(
        currentUser,
        editorsArr,
        commentsPermissions,
        createNewCommentButton,
        editorName,
      );
      editorsArr.forEach((editor) => {
        editorReadyHandler(editor, toolbarManager, setEditorStatus);
      });

      setupSuggestionUpdateHandler(currentUser, editorsArr);
      setupClickListener(editorsArr, setAttachmentForPreview);
      if (setDidReceiveDisconnectionEvent) {
        setupEditorConnectionClosedHandler(
          context.plugins.get('WebSocketGateway') as any,
          setDidReceiveDisconnectionEvent,
        );
      }
      const tabPanels = document.getElementById('document-overview-tab-panels');

      refreshDisplayMode({
        instances: editorsArr,
        windowWidth: (tabPanels && tabPanels.offsetWidth) ?? undefined,
        forceNarrow: false,
        breakpointOverride: 1000,
      });
      const targetIdentifierPredicate = (annotation: Annotation): boolean => {
        if (annotation.target && isHTMLElementArray(annotation.target)) {
          return annotation.target.some(
            (element: any) =>
              element.getAttribute('data-comment') === targetId ||
              element.getAttribute('data-suggestion') === targetId,
          );
        } else {
          return false;
        }
      };

      setEditorStatus(EditorStatus.STABLE);
      if (targetId) {
        handleHighlighting(
          context.plugins.get('Annotations'),
          targetIdentifierPredicate,
          `[data-comment="${targetId}"], [data-suggestion="${targetId}"]`,
        );
      }
    } catch (error) {
      logCustomError(EDITOR_CONTEXT_READY_ERROR, {
        error,
        companyId: currentUser.companyId,
      });
    }
  };
};

/**
 * Stops the comments archive ("comment history") dialog moving off-screen
 * while the user scrolls a multiroot document.
 */
const freezeCommentsArchiveDialogPosition = (editor: MultiRootEditor): void => {
  const dialog = editor.plugins.get('Dialog') as any;
  if (dialog?.isOpen && dialog.id === 'commentsArchive' && dialog.view) {
    dialog.view.wasMoved = true;
  }
};

export const setupCommentControlButtons = (
  currentUser: CurrentUser,
  editor: MultiRootEditor,
  commentsPermissions: number,
  createNewCommentButtonName: string,
  editorName: EditorName,
): void => {
  const commentButtons = getHTMLElements(
    `div#${createNewCommentButtonName} button`,
  );
  const addCommentButton = commentButtons[0];
  const commentHistoryButton = commentButtons[1];
  const commentButtonContainer = getHTMLElement(
    `div#${createNewCommentButtonName} div#commentButtonsContainer`,
  );
  const commentDiv = getHTMLElement(`div#${createNewCommentButtonName}`);
  // we do not have to set up the button for adding comments if a user has read-only permissions for commenting
  if (commentsPermissions < 2) {
    return;
  }
  commentDiv?.classList.remove('hidden');
  if (commentButtonContainer) {
    setElementDisabledState(addCommentButton, true);
    const editorContent = getHTMLElement(`#${editorName}`);
    if (editorContent) {
      editor.ui?.view.listenTo(
        editorContent,
        'mouseup',
        (_evt: EventInfo, domEvent: MouseEvent) => {
          const offsetY =
            domEvent.clientY -
            editorContent.getBoundingClientRect().top +
            editorContent.scrollTop;

          commentButtonContainer.style.marginTop =
            Math.max(offsetY - commentButtonContainer.clientHeight / 2, 20) +
            'px';
        },
      );
    }

    const addCommentThreadCommand = editor.commands.get('addCommentThread');
    const CommentsArchiveUIPlugin = editor.plugins.get(
      'CommentsArchiveUI',
    ) as any;
    addCommentThreadCommand?.on(
      'change:hasContent',
      (_eventInfo, _name, value) => {
        setElementDisabledState(addCommentButton, !value);
      },
    );

    editor.ui?.view.listenTo(addCommentButton, 'click', () => {
      addCommentThreadCommand?.execute();
    });

    editor.ui?.view.listenTo(commentHistoryButton, 'click', () => {
      CommentsArchiveUIPlugin._toggleDialog();
      freezeCommentsArchiveDialogPosition(editor);
    });

    const commentsRepository = editor.plugins.get('CommentsRepository') as any;
    commentsRepository?.on(
      'addComment',
      (evt: EventInfo, data: CommentsRepository.CommentData) => {
        handleComment(evt, data, editor.model.markers, currentUser);
      },
      {
        priority: 'highest',
      },
    );
  }
};

export const setupCommentButton = (
  currentUser: CurrentUser,
  editors: Editor[],
  commentsPermissions: number,
  createNewCommentButtonName: string,
  editorName: EditorName,
): void => {
  const commentButton = getHTMLElement(
    `div#${createNewCommentButtonName} button`,
  );
  const commentButtonParagraph = getHTMLElement(
    `div#${createNewCommentButtonName} p`,
  );
  const commentDiv = getHTMLElement(`div#${createNewCommentButtonName}`);
  // we do not have to set up the button for adding comments if a user has read-only permissions for commenting
  if (commentsPermissions < 2) {
    return;
  }
  commentDiv?.classList.remove('hidden');
  if (commentButton && commentButtonParagraph) {
    setElementDisabledState(commentButton, true);
    const editorContent = getHTMLElement(`#${editorName}`);
    if (editorContent) {
      editors[0]?.ui?.view.listenTo(
        editorContent,
        'mouseup',
        (_evt: EventInfo, domEvent: MouseEvent) => {
          const offsetY =
            domEvent.clientY -
            editorContent.getBoundingClientRect().top +
            editorContent.scrollTop;

          commentButtonParagraph.style.marginTop =
            Math.max(offsetY - commentButtonParagraph.clientHeight / 2, 20) +
            'px';
        },
      );
    }
    editors.forEach((editor, index) => {
      const addCommentThreadCommand = editor.commands.get('addCommentThread');
      addCommentThreadCommand?.on(
        'change:hasContent',
        (_eventInfo, _name, value) => {
          setElementDisabledState(commentButton, !value);
          commentButton.setAttribute('data-index', index.toString());
        },
      );

      editor.ui?.view.listenTo(commentButton, 'click', () => {
        if (commentButton.getAttribute('data-index') === index.toString())
          addCommentThreadCommand?.execute();
      });

      const commentsRepository = editor.plugins.get(
        'CommentsRepository',
      ) as any;
      commentsRepository?.on(
        'addComment',
        (evt: EventInfo, data: CommentsRepository.CommentData) => {
          handleComment(evt, data, editor.model.markers, currentUser);
        },
        {
          priority: 'highest',
        },
      );
    });
  }
};

export const setupClickListener = (
  editors: Editor[],
  setAttachmentForPreview: (id: string) => void,
): void => {
  editors.forEach((editor) => {
    editor.editing.view.document.on(
      'click',
      handleClick(editor, setAttachmentForPreview),
    );
    editor.editing.view.document.on(
      'selectionChangeDone',
      handleSelectionChange(editor),
    );
  });
};

export const setupAttachmentPreviewListener = (
  contentElement: Element,
  handleAttachmentClick: (attachmentId: string) => void,
): void => {
  const anchors = contentElement.getElementsByTagName('a');
  for (const anchor of anchors) {
    anchor.addEventListener('click', (evt) => {
      evt.preventDefault();
      const href = (evt.currentTarget as any).href;
      if (!href) {
        return;
      }

      const attachmentId = extractAttachmentIdFromHref(href);
      if (attachmentId && handleAttachmentClick) {
        handleAttachmentClick(attachmentId);
        return;
      }

      const bookmarkTarget = getBookmarkTarget(href);
      if (bookmarkTarget) {
        scrollToBookmarkTarget(bookmarkTarget);
        return;
      }

      window.open(href, '_blank');
    });
  }
};

export const setupImagePreviewListener = (
  contentElement: Element,
  handleImageClick: (attachmentId: string) => void,
): void => {
  const images = contentElement.getElementsByTagName('img');
  for (const image of images) {
    image.addEventListener('click', () => {
      const attachmentId = extractAttachmentIdFromHref(image.src);
      if (attachmentId) {
        handleImageClick(attachmentId);
      }
    });
  }
};

const handleClick =
  (editor: Editor, setAttachmentForPreview: (id: string) => void) =>
  (
    evt: any,
    data: {
      target?: ViewElement;
      domEvent?: PointerEvent;
    },
  ) => {
    const clickedViewElement = data.target;
    if (!clickedViewElement) {
      return;
    }
    if (clickedViewElement.is('$text')) {
      return;
    }

    const elementUrl =
      clickedViewElement?.getAttribute('href') ||
      clickedViewElement?.getAttribute('src');
    const isSmartlink = clickedViewElement?.hasClass('docreference');

    if (isSmartlink && elementUrl) {
      data.domEvent?.preventDefault();
      data.domEvent?.stopPropagation();
      window.open(elementUrl, '_blank');
      return;
    }

    const isCommentOrReadOnlyEditor =
      isCommentsOnlyMode(editor) || editor.isReadOnly;

    const isPreviewableElement =
      (clickedViewElement.name === 'a' || clickedViewElement.name === 'img') &&
      elementUrl;

    if (isCommentOrReadOnlyEditor && isPreviewableElement) {
      data.domEvent?.preventDefault();
      data.domEvent?.stopPropagation();
      handleLinkClick(elementUrl, setAttachmentForPreview);
      return;
    }
    // open/download links/attachments that are commented on and/or are styled spans or bold
    if (
      (clickedViewElement.name === 'span' ||
        clickedViewElement.name === 'strong' ||
        clickedViewElement.name === 'i' ||
        clickedViewElement.name === 's' ||
        clickedViewElement.name === 'u') &&
      isCommentOrReadOnlyEditor
    ) {
      data.domEvent?.preventDefault();
      data.domEvent?.stopPropagation();
      openPotentiallyClickedAnchor(clickedViewElement, setAttachmentForPreview);
      return;
    }
  };

const handleSelectionChange = (editor: Editor) => () => {
  // disable comment button when input is only selected element
  const selectedElement =
    editor.editing.view.document.selection.getSelectedElement();
  if (selectedElement?.name === 'input') {
    const addCommentThreadCommand = editor.commands.get('addCommentThread');
    addCommentThreadCommand?.fire('change:isEnabled', false);
  }
};

const openPotentiallyClickedAnchor = (
  clickedViewElement: ViewElement,
  setAttachmentForPreview: (id: string) => void,
): void => {
  const href = CKEditorUtils.getFirstParentHref(clickedViewElement);
  if (!href) {
    return;
  }
  handleLinkClick(href, setAttachmentForPreview);
};

const handleLinkClick = (
  url: string,
  setAttachmentForPreview: (id: string) => void,
): void => {
  const attachmentId = extractAttachmentIdFromHref(url);
  if (attachmentId) {
    setAttachmentForPreview(attachmentId);
    return;
  }

  const bookmarkTarget = getBookmarkTarget(url);
  if (bookmarkTarget) {
    scrollToBookmarkTarget(bookmarkTarget);
    return;
  }

  // open links that are not inline attachments in a new tab in comment- or read-only mode
  window.open(url, '_blank');
};

export const setupEditorConnectionClosedHandler = (
  webSocketGateway: ContextPlugin,
  setDidReceiveDisconnectionEvent: (didReceive: boolean) => void,
) => {
  webSocketGateway.on(
    'change:state',
    (eventInfo: any, _name: any, value: any, _oldValue: any) => {
      if (!eventInfo?.source?._isBrowserOffline && value === 'disconnected') {
        setDidReceiveDisconnectionEvent(true);
      }
    },
  );
};

const setupMode = (
  editor: MultiRootEditor,
  documentStatus: string,
  commentsPermissions: number,
  isEditorDoneEditing: boolean,
  isEditorOwner: boolean,
) => {
  // Ghost users who have read only access to comments
  if (commentsPermissions < 2) {
    setCommentsOnlyModeTo([editor], true);
    toggleCheckboxMode(false);
    setCommentsOnlyModeTo([editor], true);
  }
  if (documentStatus === 'for_review') {
    toggleTrackChanges([editor], EditorMode.TRACKCHANGES);
  }

  if (documentStatus === 'for_approval') {
    setCommentsOnlyModeTo([editor], true);
    toggleCheckboxMode(false);
    setCommentsOnlyModeTo([editor], true);
  }

  if (isEditorDoneEditing && !isEditorOwner) {
    setCommentsOnlyModeTo([editor], true);
    toggleCheckboxMode(false);
    setCommentsOnlyModeTo([editor], true);
  }
};

function configureAutosaveStateHandler(
  editor: Editor,
  setEditorStatus: React.Dispatch<SetStateAction<EditorStatus>>,
) {
  editor.plugins
    .get('PendingActions')
    .on('change:hasAny', (evt, propertyName, newValue) => {
      if (newValue) {
        setEditorStatus((prev) =>
          prev === EditorStatus.ERROR ? prev : EditorStatus.PENDING_AUTOSAVE,
        );
      } else {
        setEditorStatus((prev) =>
          prev === EditorStatus.ERROR ? prev : EditorStatus.SAVED,
        );
      }
    });
}

export const editorReadyHandler = (
  editor: Editor,
  toolbarManager: ToolbarManager,
  setEditorStatus: React.Dispatch<SetStateAction<EditorStatus>>,
) => {
  try {
    setCommentsOnlyModeTo([editor], true);
    toolbarManager.configureEditorToShowToolbarOnFocus(editor);
    toolbarManager.hideToolbar(editor);
    CKEditorUtils.configureEditorDelete(editor);
    enableUploadAdapter(editor);
    configureQualioPluginsForTrackChanges(editor);
    mathCharactersExtended(editor);
    greekCharactersExtended(editor);
    editor.model.schema.setAttributeProperties('htmlAttributes', {
      isFormatting: true,
    });
    configureBalloonPanels(editor);
    configureAutosaveStateHandler(editor, setEditorStatus);
  } catch (error) {
    logCustomError(EDITOR_READY_ERROR, {
      error,
    });
  }
};

export const multiRootEditorReadyHandler = (
  editor: MultiRootEditor,
  editorName: EditorName,
  currentUser: CurrentUser,
  commentsPermissions: number,
  createNewCommentButton: string,
  orientation: PageOrientation,
  documentStatus: string,
  setEditorStatus: React.Dispatch<SetStateAction<EditorStatus>>,
  handleHighlighting: (
    annotations: any,
    identifierPredicate: (annotation: Annotation) => boolean,
    htmlSelector: string,
  ) => NodeJS.Timeout,
  setAttachmentForPreview: (id: string) => void,
  documentId: number,
  setDidReceiveDisconnectionEvent?: (didReceive: boolean) => void,
  targetId?: string,
  reusableContentEnabled?: boolean,
  isEditorDoneEditing = false,
  isEditorOwner = false,
) => {
  try {
    setupMode(
      editor,
      documentStatus,
      commentsPermissions,
      isEditorDoneEditing,
      isEditorOwner,
    );
    setupCommentControlButtons(
      currentUser,
      editor,
      commentsPermissions,
      createNewCommentButton,
      editorName,
    );
    if (commentsPermissions >= 2) {
      setupDeletedByUserHandlers(
        editor,
        currentUser,
        currentUser.companyId,
        documentId,
      );
    }
    // TODO: Remove and re-test with ToC once upgraded to CKEditor 47.2.0+
    editor.plugins.get('DragDrop').forceDisabled('multiroot');
    CKEditorUtils.configureEditorDelete(editor as any);
    enableUploadAdapter(editor as any);
    configureQualioPluginsForTrackChanges(editor as any);
    mathCharactersExtended(editor as any);
    greekCharactersExtended(editor as any);
    editor.model.schema.setAttributeProperties('htmlAttributes', {
      isFormatting: true,
    });
    configureBalloonPanels(editor as any);
    configureAutosaveStateHandler(editor as any, setEditorStatus);
    configureChangedRootsTracker(editor as any);
    setupSuggestionUpdateHandler(currentUser, [editor as any]);
    setupClickListener([editor as any], setAttachmentForPreview);

    if (setDidReceiveDisconnectionEvent) {
      setupEditorConnectionClosedHandler(
        editor.plugins.get('WebSocketGateway') as any,
        setDidReceiveDisconnectionEvent,
      );
    }
    const tabPanels = document.getElementById('document-overview-tab-panels');

    refreshDisplayMode({
      instances: [editor as any],
      windowWidth: (tabPanels && tabPanels.offsetWidth) ?? undefined,
      forceNarrow: orientation === PageOrientation.Landscape,
      breakpointOverride: 1000,
    });
    const targetIdentifierPredicate = (annotation: Annotation): boolean => {
      if (annotation.target && isHTMLElementArray(annotation.target)) {
        return annotation.target.some(
          (element: any) =>
            element.getAttribute('data-comment') === targetId ||
            element.getAttribute('data-suggestion') === targetId,
        );
      } else {
        return false;
      }
    };

    setEditorStatus(EditorStatus.STABLE);
    if (reusableContentEnabled) {
      if (documentStatus === 'draft') {
        CKReusableContent.ReusableContent.updateEmbedUrlsInContent(editor);
      }
    }
    if (targetId) {
      handleHighlighting(
        editor.plugins.get('Annotations'),
        targetIdentifierPredicate,
        `[data-comment="${targetId}"], [data-suggestion="${targetId}"]`,
      );
    }
  } catch (error) {
    logCustomError(EDITOR_CONTEXT_READY_ERROR, {
      error,
      companyId: currentUser.companyId,
    });
  }
};

export const enableCKEditorMode = (
  mode: EditorMode,
  editors: Editor[],
  toolbarManager: ToolbarManager,
  inEditModeCallback: (inEditMode: boolean) => void,
) => {
  const inEditMode = mode !== EditorMode.VIEW;

  toggleCheckboxMode(mode === EditorMode.EDIT);
  setCommentsOnlyModeTo(editors, !inEditMode);
  inEditModeCallback(inEditMode);

  if (inEditMode) {
    // focus and show first editor
    editors[0].focus();
    toolbarManager.setToolbarFromEditor(editors[0]);
    toggleTrackChanges(editors, mode);
  } else {
    editors.forEach((editor) => {
      toolbarManager.hideToolbar(editor);
    });
  }
};

export function mathCharactersExtended(editor: Editor): void {
  const scp = editor.plugins.get('SpecialCharacters');
  scp.addItems('Mathematical', [{ title: 'Delta', character: 'Δ' }]);
}

export function greekCharactersExtended(editor: Editor): void {
  const scp = editor.plugins.get('SpecialCharacters');
  scp.addItems(
    'Greek',
    [
      { title: 'Alpha', character: 'Α' },
      { title: 'alpha', character: 'α' },
      { title: 'Beta', character: 'Β' },
      { title: 'beta', character: 'β' },
      { title: 'Gamma', character: 'Γ' },
      { title: 'gamma', character: 'γ' },
      { title: 'Delta', character: 'Δ' },
      { title: 'delta', character: 'δ' },
      { title: 'Epsilon', character: 'Ε' },
      { title: 'epsilon', character: 'ε' },
      { title: 'Zeta', character: 'Ζ' },
      { title: 'zeta', character: 'ζ' },
      { title: 'Eta', character: 'Η' },
      { title: 'eta', character: 'η' },
      { title: 'Theta', character: 'Θ' },
      { title: 'theta', character: 'θ' },
      { title: 'Iota', character: 'Ι' },
      { title: 'iota', character: 'ι' },
      { title: 'Kappa', character: 'Κ' },
      { title: 'kappa', character: 'κ' },
      { title: 'Lambda', character: 'Λ' },
      { title: 'lambda', character: 'λ' },
      { title: 'Mu', character: 'Μ' },
      { title: 'mu', character: 'μ' },
      { title: 'Nu', character: 'Ν' },
      { title: 'nu', character: 'ν' },
      { title: 'Xi', character: 'Ξ' },
      { title: 'xi', character: 'ξ' },
      { title: 'Omicron', character: 'Ο' },
      { title: 'omicron', character: 'ο' },
      { title: 'Pi', character: 'Π' },
      { title: 'pi', character: 'π' },
      { title: 'Rho', character: 'Ρ' },
      { title: 'rho', character: 'ρ' },
      { title: 'Sigma', character: 'Σ' },
      { title: 'sigma', character: 'σ' },
      { title: 'Tau', character: 'Τ' },
      { title: 'tau', character: 'τ' },
      { title: 'Upsilon', character: 'Υ' },
      { title: 'upsilon', character: 'υ' },
      { title: 'Phi', character: 'Φ' },
      { title: 'phi', character: 'φ' },
      { title: 'Chi', character: 'Χ' },
      { title: 'chi', character: 'χ' },
      { title: 'Psi', character: 'Ψ' },
      { title: 'psi', character: 'ψ' },
      { title: 'Omega', character: 'Ω' },
      { title: 'omega', character: 'ω' },
    ],
    { label: 'Greek' },
  );
}

function isHTMLElementArray(
  value: Rect | HTMLElement[],
): value is HTMLElement[] {
  return (
    Array.isArray(value) && value.every((item) => item instanceof HTMLElement)
  );
}
